import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from '@/app/providers/globalState';
import { useAuth } from '@/features/auth/AuthContext';
import { PlusOutlined } from '@ant-design/icons';
import { Dropdown, Input, Tooltip } from 'antd';
import { TruncatedText, SparkIcon, BreadcrumbChevron, ReadOnlyGuard, useReadOnly } from '@spark';
import { buildTaskNavigation } from '@/features/tasks/utils/navigation';
import '@/features/tasks/styles/UsecaseHeader.css';

/**
 * "All Projects / <project> / <task>" breadcrumb with the project + task
 * switcher dropdowns (search, Add Task, Create New Project footers).
 *
 * Extracted from `UsecaseHeader` so both the global header (off-task screens)
 * and the task page header render the same navigation contract.
 */
const TaskBreadcrumb = ({ onCreateProject, onAddTask }) => {
    const navigate = useNavigate();
    const { role } = useAuth();
    const { readOnly } = useReadOnly();
    const {
        getCurrentUsecase,
        setCurrentUsecaseId,
        currentSubGroup,
        allApps,
        evaluationResultsReady,
    } = useContext(GlobalContext);
    const currentUsecase = getCurrentUsecase();
    const [projectDropdownVisible, setProjectDropdownVisible] = useState(false);
    const [taskDropdownVisible, setTaskDropdownVisible] = useState(false);
    const [taskSearchValue, setTaskSearchValue] = useState('');
    const [projectSearchValue, setProjectSearchValue] = useState('');

    useEffect(() => {
        if (!projectDropdownVisible) {
            setProjectSearchValue('');
        }
    }, [projectDropdownVisible]);

    useEffect(() => {
        if (!taskDropdownVisible) {
            setTaskSearchValue('');
        }
    }, [taskDropdownVisible]);

    const projectsByName = useMemo(() => {
        const grouped = {};
        (allApps || []).forEach((app) => {
            grouped[app.group_name] = app;
        });
        return grouped;
    }, [allApps]);

    const filteredProjectNames = useMemo(() => {
        const query = projectSearchValue.trim().toLowerCase();
        const names = Object.keys(projectsByName);
        if (!query) return names;
        return names.filter((name) => (name || '').toLowerCase().includes(query));
    }, [projectsByName, projectSearchValue]);

    const siblingTaskRows = useMemo(() => {
        if (!currentUsecase) return [];
        const project = projectsByName[currentUsecase.group_name];
        const subGroups = project?.sub_groups || [];
        const q = taskSearchValue.trim().toLowerCase();
        if (!q) return subGroups;
        return subGroups.filter((sg) => (sg.sub_group_name || '').toLowerCase().includes(q));
    }, [currentUsecase, projectsByName, taskSearchValue]);

    if (!currentUsecase) return null;

    const { group_name, usecase_name } = currentUsecase;
    const taskCrumbLabel =
        (currentSubGroup?.sub_group_name || currentUsecase.sub_group_name || usecase_name || '').trim() ||
        usecase_name;
    const activeSubGroupName =
        (currentSubGroup?.sub_group_name || currentUsecase.sub_group_name || '').trim();

    const resolveTaskUsecase = (subGroup) => {
        const usecases = subGroup?.usecases || [];
        return usecases.find((u) => u._id === currentUsecase._id) || usecases[0] || null;
    };

    const handleBack = () => navigate('/');

    // Reuses the same navigation contract as the Projects card/row: a draft
    // resumes the creation wizard at the saved step; a live task lands on the
    // correct step view (Model Configuration / Data Editor / Training /
    // Evaluation) instead of the bare task landing. `siblingUsecases` are the
    // objectives of the picked task's sub-group.
    const handleTaskSelect = (targetUsecase, siblingUsecases) => {
        setProjectDropdownVisible(false);
        setTaskDropdownVisible(false);
        const nav = buildTaskNavigation(targetUsecase, siblingUsecases, {
            evaluationResultsReady,
        });
        if (!nav) return;
        setCurrentUsecaseId(nav.usecaseId);
        navigate(nav.url, { replace: true, ...(nav.state ? { state: nav.state } : {}) });
    };

    const renderFooterAction = ({ disabled, disabledTooltip, text, onClick }) => {
        if (disabled) {
            return (
                <Tooltip title={disabledTooltip} placement="right">
                    <div className="breadcrumb-dropdown-footer-item breadcrumb-dropdown-footer-item-disabled">
                        <PlusOutlined /> {text}
                    </div>
                </Tooltip>
            );
        }

        return (
            <ReadOnlyGuard placement="right">
                <div className="breadcrumb-dropdown-footer-item" onClick={onClick}>
                    <PlusOutlined /> {text}
                </div>
            </ReadOnlyGuard>
        );
    };

    const createTaskMenuItems = (projectName, subGroups) => {
        const baseItems = (subGroups || []).map((subGroup) => {
            const usecases = subGroup.usecases || [];
            const activeUsecase = usecases.find((u) => u._id === currentUsecase._id);
            const targetUsecase = activeUsecase || usecases[0];
            const isActive = Boolean(activeUsecase);
            const taskLabel = subGroup.sub_group_name || targetUsecase?.usecase_name || '';
            return {
                key: `${projectName}__${subGroup.sub_group_name}`,
                disabled: !targetUsecase,
                label: (
                    <div
                        className={`breadcrumb-dropdown-item ${isActive ? 'breadcrumb-dropdown-item-active' : ''}`}
                    >
                        <TruncatedText
                            text={taskLabel}
                            maxWidth={260}
                            style={{ flex: 1, minWidth: 0, marginRight: 16 }}
                        />
                    </div>
                ),
                onClick: () => {
                    if (targetUsecase) {
                        handleTaskSelect(targetUsecase, usecases);
                    }
                },
            };
        });

        const addTaskItem = {
            key: `add_task__${projectName}`,
            disabled: readOnly || role === 'Viewer',
            label: (
                <div className="breadcrumb-dropdown-item breadcrumb-dropdown-add">
                    <span className="breadcrumb-dropdown-add-text">
                        <PlusOutlined /> Add Task
                    </span>
                </div>
            ),
            onClick: () => {
                setProjectDropdownVisible(false);
                if (typeof onAddTask === 'function') {
                    onAddTask(projectName);
                }
            },
        };

        return [...baseItems, addTaskItem];
    };

    const createProjectMenuItems = () => {
        return filteredProjectNames.map((projectName) => {
            const project = projectsByName[projectName];
            const subGroups = project?.sub_groups || [];

            return {
                key: projectName,
                label: (
                    <div
                        className={`breadcrumb-dropdown-item breadcrumb-dropdown-project ${projectName === group_name ? 'breadcrumb-dropdown-item-active' : ''}`}
                    >
                        <TruncatedText
                            text={projectName}
                            maxWidth={260}
                            style={{ flex: 1, minWidth: 0, marginRight: 16 }}
                        />
                    </div>
                ),
                children: createTaskMenuItems(projectName, subGroups),
            };
        });
    };

    const projectMenu = createProjectMenuItems();

    return (
        <nav className="spark-breadcrumb" aria-label="Breadcrumb">
            <ol className="spark-breadcrumb-list">
                <li className="spark-breadcrumb-item-wrapper">
                    <a
                        href="/"
                        className="spark-breadcrumb-item spark-breadcrumb-item-link spark-breadcrumb-item-link-navigation fs-unmask"
                        onClick={(e) => { e.preventDefault(); handleBack(); }}
                    >
                        <span className="spark-breadcrumb-icon">
                            <SparkIcon slug="home" size="small" />
                        </span>
                        <span className="spark-breadcrumb-label">All Projects</span>
                    </a>
                </li>
                <li className="spark-breadcrumb-item-wrapper">
                    <span className="spark-breadcrumb-separator spark-breadcrumb-separator-default spark-breadcrumb-separator-slash">
                        /
                    </span>
                    <Dropdown
                        menu={{ items: projectMenu }}
                        trigger={['click']}
                        open={projectDropdownVisible}
                        onOpenChange={(open) => setProjectDropdownVisible(open)}
                        placement="bottomLeft"
                        dropdownRender={(menu) => (
                            <div className="breadcrumb-dropdown-container breadcrumb-project-dropdown">
                                <div className="breadcrumb-dropdown-search">
                                    <Input
                                        size="middle"
                                        placeholder="Search projects..."
                                        value={projectSearchValue}
                                        onChange={(e) => setProjectSearchValue(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                                <div className="breadcrumb-project-dropdown-list">
                                    {filteredProjectNames.length === 0 ? (
                                        <div className="breadcrumb-dropdown-empty">No projects found</div>
                                    ) : (
                                        menu
                                    )}
                                </div>
                                {renderFooterAction({
                                    disabled: role === 'Viewer',
                                    disabledTooltip: 'Viewers cannot create new projects',
                                    text: 'Create New Project',
                                    onClick: () => {
                                        setProjectDropdownVisible(false);
                                        if (typeof onCreateProject === 'function') {
                                            onCreateProject();
                                        }
                                    },
                                })}
                            </div>
                        )}
                    >
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <a className="spark-breadcrumb-item spark-breadcrumb-item-link spark-breadcrumb-item-link-navigation">
                            <span className="spark-breadcrumb-label">{group_name}</span>
                            <BreadcrumbChevron open={projectDropdownVisible} state="default" />
                        </a>
                    </Dropdown>
                </li>
                <li className="spark-breadcrumb-item-wrapper">
                    <span className="spark-breadcrumb-separator spark-breadcrumb-separator-primary spark-breadcrumb-separator-slash">
                        /
                    </span>
                    <Dropdown
                        trigger={['click']}
                        open={taskDropdownVisible}
                        onOpenChange={(open) => setTaskDropdownVisible(open)}
                        placement="bottomLeft"
                        dropdownRender={() => (
                            <div className="breadcrumb-dropdown-container breadcrumb-task-dropdown">
                                <div className="breadcrumb-dropdown-search">
                                    <Input
                                        size="middle"
                                        placeholder="Search tasks..."
                                        value={taskSearchValue}
                                        onChange={(e) => setTaskSearchValue(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                                <div className="breadcrumb-task-dropdown-list">
                                    {siblingTaskRows.length === 0 ? (
                                        <div className="breadcrumb-dropdown-empty">No tasks found</div>
                                    ) : (
                                        siblingTaskRows.map((subGroup) => {
                                            const targetUsecase = resolveTaskUsecase(subGroup);
                                            const isActive = subGroup.sub_group_name === activeSubGroupName;
                                            return (
                                                <div
                                                    key={subGroup.sub_group_name}
                                                    className={`breadcrumb-dropdown-item ${isActive ? 'breadcrumb-dropdown-item-active' : ''}`}
                                                    onClick={() => {
                                                        if (targetUsecase) {
                                                            handleTaskSelect(
                                                                targetUsecase,
                                                                subGroup.usecases || []
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <TruncatedText
                                                        text={subGroup.sub_group_name}
                                                        maxWidth={260}
                                                        style={{ flex: 1, minWidth: 0, marginRight: 16 }}
                                                    />
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                                <div className="breadcrumb-dropdown-divider" />
                                {renderFooterAction({
                                    disabled: role === 'Viewer',
                                    disabledTooltip: 'Viewers cannot add tasks',
                                    text: 'Add Task',
                                    onClick: () => {
                                        setTaskDropdownVisible(false);
                                        if (typeof onAddTask === 'function') {
                                            onAddTask(group_name);
                                        }
                                    },
                                })}
                            </div>
                        )}
                    >
                        <span className="spark-breadcrumb-item spark-breadcrumb-item-active">
                            <span className="spark-breadcrumb-label">{taskCrumbLabel}</span>
                            <BreadcrumbChevron open={taskDropdownVisible} state="active" />
                        </span>
                    </Dropdown>
                </li>
            </ol>
        </nav>
    );
};

export default TaskBreadcrumb;
